/** Sticky test-render context, per notification: the chosen source (pasted
 *  JSON / delivery-log id / entity id) survives dialog reopens and is shared
 *  between the TestRenderDialog and the docked live preview. The page lifts
 *  this state and passes it to both; sessionStorage (keyed by notification id)
 *  keeps it across in-tab navigation without leaking between notifications.
 */
import { useCallback, useState } from 'react';

import { EMPTY_CONTEXT_SOURCE, type ContextSourceValue } from './ContextSourcePicker';

const keyFor = (notificationId: number) => `nf-test-render-ctx-${notificationId}`;

function load(notificationId: number): ContextSourceValue {
  try {
    const raw = sessionStorage.getItem(keyFor(notificationId));
    if (!raw) return EMPTY_CONTEXT_SOURCE;
    return { ...EMPTY_CONTEXT_SOURCE, ...(JSON.parse(raw) as Partial<ContextSourceValue>) };
  } catch {
    return EMPTY_CONTEXT_SOURCE;
  }
}

export function useStickyRenderContext(
  notificationId: number,
): [ContextSourceValue, (v: ContextSourceValue) => void] {
  const [source, setSource] = useState<ContextSourceValue>(() => load(notificationId));
  const set = useCallback(
    (v: ContextSourceValue) => {
      setSource(v);
      try {
        sessionStorage.setItem(keyFor(notificationId), JSON.stringify(v));
      } catch {
        /* quota / private mode — in-memory state still works */
      }
    },
    [notificationId],
  );
  return [source, set];
}
